import {
  AfterContentInit,
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ContentChild,
  ContentChildren,
  ElementRef,
  EventEmitter,
  HostBinding,
  Input,
  inject,
  NgZone,
  OnChanges,
  OnDestroy,
  Output,
  QueryList,
  SimpleChanges,
  ViewChild,
  OnInit,
} from '@angular/core';
import { Subscription } from 'rxjs';
import { take } from 'rxjs/operators';

import { MenuItem } from 'primeng/api';
import { TieredMenu } from 'primeng/tieredmenu';

import { TrColumn } from '@shared/restaurant/models';
import { OrderByValues } from '@tablesready/common-ui';
import { FrontendModalService } from '@tablesready/frontend-modal';

import { TrTableStateService } from '../../services/tr-table-state.service';
import { TrTableLayoutModeService } from '../../services/tr-table-layout-mode.service';
import {
  TrTableRegistryService,
  TrTableSettingsHost,
} from '../../services/tr-table-registry.service';
import { TrCellDirective } from './tr-cell.directive';
import { TrHeaderCellDirective } from './tr-header-cell.directive';
import { TrGroupHeaderDirective } from './tr-group-header.directive';
import { ColumnVisibilityModalComponent } from '../column-visibility-modal/column-visibility-modal.component';
import { TrFillHeight } from './tr-fill-height';

/**
 * Columns with their widths left unset.
 *
 * Widths are a device-only preference, so they never cross the location boundary in
 * either direction: a published default carries none, and a default read back carries
 * none either (defaults saved before this rule may still hold some). A device that
 * hasn't sized a column itself gets the content-based default — the size Auto Fit and
 * Reset Width return to.
 */
const withoutWidths = (columns: TrColumn[]): TrColumn[] => columns.map(col => ({
  ...col,
  width: undefined,
}));

/**
 * Generic, config-driven table shell (PrimeNG-backed).
 *
 * Columns are described as data (`TrColumn[]`) and rendered generically; each cell
 * and header is supplied by the host via projected `*trCell` / `*trHeaderCell`
 * templates, so the same shell renders parties, customers, or any row type. The
 * shell owns everything else: the per-column options menu (Sort, Pin, Auto Fit,
 * Reset Width, Hide), pointer-based drag-reorder and resize, the frozen (sticky)
 * offset math for pinned columns, optional day-style group rows, and — in
 * scrollable mode — internal row windowing via `loadMore`.
 *
 * Table-wide actions (Show/Hide Columns and the location defaults) live in the
 * page toolbar's gear button instead: `<tablesready-table-settings>` finds this
 * table by `stateId` through {@link TrTableRegistryService} and drives the
 * {@link TrTableSettingsHost} methods below. That gear also owns the
 * layout-editing mode ({@link TrTableLayoutModeService}): the ⋮ menus and
 * drag-to-reorder only appear while it is on, so a resting header carries just
 * labels, sort state and the resize grips.
 *
 * Column state (order, visibility, pin, width) is a *device* preference: bind
 * `stateId` + `locationId` and every change is written straight to localStorage
 * via {@link TrTableStateService}, then merged back over the host's freshly built
 * columns on each rebuild. Hosts therefore stay presentational — they describe
 * which columns exist, never what the user did to them.
 *
 * Two layout modes:
 * - `scrollable=true` (default): PrimeNG owns a scroll viewport of height
 *   `scrollHeight`; bind `hasMore` and handle `loadMore` for windowing.
 * - `scrollable=false`: a plain content-height table; the host page scrolls
 *   (ion-infinite-scroll) or paginates. Wrap the table in an `overflow-x:auto`
 *   container so sticky pinned columns still work horizontally.
 */
@Component({
  selector: 'tablesready-tr-table',
  templateUrl: './tr-table.component.html',
  styleUrls: ['./tr-table.component.scss', './tr-table-grid.scss'],
  standalone: false,
})
export class TrTableComponent<T = unknown>
  implements OnInit, AfterContentInit, AfterViewInit, OnChanges, OnDestroy, TrTableSettingsHost
{
  /** Row data. Rows for which `rowHidden` returns true are rendered but visually collapsed. */
  @Input() rows: T[] = [];
  /** All columns (respecting the host's settings), including hidden ones. */
  @Input() columns: TrColumn[] = [];
  /**
   * Stable id of this table (e.g. `'waitlist'`). Together with `locationId` it
   * keys the device's saved column state; leave unset to disable persistence.
   */
  @Input() stateId?: string;
  /** Location the saved column state belongs to (state is per location). */
  @Input() locationId?: string | null;
  /** PrimeNG dataKey + default trackBy field. */
  @Input() dataKey = '_id';
  /** Row trackBy; defaults to `row[dataKey]`. */
  @Input() trackBy?: (index: number, row: T) => unknown;

  /** Active sort key (matches a column's `sortKey`). */
  @Input() sortBy = '';
  /** Direction of the active sort. Each host maps its own reverse flag onto this. */
  @Input() sortAscending = true;

  /** Owns an internal scroll viewport (true) vs. laying out at content height (false). */
  @Input() scrollable = true;
  /**
   * Height of the PrimeNG scroll viewport when `scrollable`. `'flex'` fills the parent;
   * `'fill'` measures the rest of the page below the table (see {@link TrFillHeight}).
   */
  @Input() scrollHeight = '60vh';
  /** True when the host still has rows beyond the current `rows` slice (scrollable mode). */
  @Input() hasMore = false;

  @Input() displayCompact = false;
  /** Show drag handles / allow resize + auto-fit. */
  @Input() resizableColumns = true;
  @Input() emptyMessage = 'No records.';

  /**
   * Force the narrow-screen list layout on or off, instead of letting the
   * viewport width decide.
   *
   * Leave unset for the normal behaviour (list below {@link LIST_BREAKPOINT_PX}).
   * `true` pins a host to the list layout regardless of width — the native iOS
   * shell, or a page that is deliberately laid out as a list — and `false` keeps
   * columns on a phone.
   */
  @Input() listView?: boolean;

  /** Row id emitted as the `<tr>` id (for scroll-to-row / highlight). */
  @Input() rowId?: (row: T) => string;
  /** Extra class(es) applied to a row's `<tr>`. */
  @Input() rowClass?: (row: T) => string | null | undefined;
  /** When true the row is collapsed (via the `tr-row--hidden` class). */
  @Input() rowHidden?: (row: T) => boolean;
  /** Id of the highlighted row (matched against `rowId`). */
  @Input() highlightedId: string | null = null;
  /** The location's default layout: order, visibility and pin. Widths in it are ignored. */
  @Input() columnsToSave: TrColumn[] | null = null;
  /**
   * Optional explanation per column key, shown as an info icon in the show/hide
   * columns modal. For columns whose purpose isn't obvious from the name — the
   * ones whose toggle used to live on a settings page, with the text it had there.
   */
  @Input() columnHints: Record<string, string> = {};

  /**
   * Group key for a row; a full-width group-header row (the projected
   * `*trGroupHeader`) is rendered above the first row of each new group.
   */
  @Input() groupBy?: (row: T, index: number, rows: T[]) => string | null;

  @Output() orderByChange = new EventEmitter<OrderByValues>();
  /** Emitted when the internal scroll nears the bottom and more rows should window in. */
  @Output() loadMore = new EventEmitter<void>();
  /** "Save as Location Default": the columns to publish, without widths. */
  @Output() columnsUpdated = new EventEmitter<TrColumn[]>();

  @ViewChild('colMenu') colMenu?: TieredMenu;

  @ContentChildren(TrCellDirective, { descendants: true })
  cellDirs?: QueryList<TrCellDirective>;
  @ContentChildren(TrHeaderCellDirective, { descendants: true })
  headerDirs?: QueryList<TrHeaderCellDirective>;
  @ContentChild(TrGroupHeaderDirective) groupDir?: TrGroupHeaderDirective;

  /** Rendered columns: visible, start-pinned first, end-pinned last. */
  displayColumns: TrColumn[] = [];
  /**
   * Keys of the columns currently rendered, handed to every cell template.
   *
   * A cell occasionally needs to know what the rest of the row shows: the waitlist
   * prints a party's note under its name only while the Notes column is hidden,
   * and would otherwise print it twice. Replaced (never mutated) whenever the
   * display is recomputed, so a template binding sees the change.
   */
  visibleKeys = new Set<string>();
  menuModel: MenuItem[] = [];

  /**
   * True while the app is in "edit table layout" mode (toolbar gear → Edit Table
   * Layout): the per-column ⋮ menus are rendered and headers can be dragged to
   * reorder. Off by default so the header carries only labels, sort state and the
   * resize grips. Also drives the `tr-layout-editing` host class the styles use.
   */
  @HostBinding('class.tr-layout-editing') layoutEditing = false;

  /** Projected cell / header templates by column key. */
  cellTemplates = new Map<string, TrCellDirective>();
  headerTemplates = new Map<string, TrHeaderCellDirective>();

  /**
   * Sticky `left` offset (px) for each start-pinned column, keyed by column key.
   * Owned here (not by PrimeNG's `pFrozenColumn`) so offsets stay correct the
   * instant a column is pinned, resized, or reordered.
   */
  frozenLeft: Record<string, number> = {};
  /** Sticky `right` offset (px) for each end-pinned column, keyed by column key. */
  frozenRight: Record<string, number> = {};
  /** Key of the right-most start-pinned column — gets the divider/shadow. */
  lastStartKey: string | null = null;
  /** Key of the left-most end-pinned column — gets the divider/shadow. */
  firstEndKey: string | null = null;

  /** Column currently being drag-reordered. */
  dragColKey: string | null = null;
  /** Column the drag is hovering over (drop target indicator). */
  dragOverKey: string | null = null;

  /**
   * Below this viewport width the shell renders {@link TrListComponent} instead of
   * columns. 576px is Bootstrap's `sm` floor — the same threshold the app used
   * when the old table collapsed itself via `media-breakpoint-down(xs)`, so phones
   * get the layout they had before the shared table.
   */
  private static readonly LIST_BREAKPOINT_PX = 576;
  private static readonly LOAD_MORE_THRESHOLD_PX = 300;
  private static readonly MIN_COL_WIDTH = 48;
  private static readonly AUTO_FIT_PADDING = 24;
  private static readonly DRAG_THRESHOLD_PX = 6;
  private scrollEl?: HTMLElement;
  private pendingLoad = false;
  private resizeState?: { col: TrColumn; startX: number; startWidth: number };
  private reorderState?: { col: TrColumn; startX: number; startY: number; active: boolean };
  private frozenRaf = 0;
  private contentSubs?: Subscription;
  /** Viewport width watcher driving {@link listMode}. */
  private viewportQuery?: MediaQueryList;
  /** Latest answer from {@link viewportQuery}; the `listView` input can override it. */
  private narrowViewport = false;
  /** List mode only: the column layout is also rendered off-screen to print from. */
  printSource = false;
  /** Measured outer height for `scrollHeight="fill"`; unset until the first measurement. */
  private fillHeightPx?: number;
  private fillHeight?: TrFillHeight;
  /**
   * Pristine copy of the last `columns` input — the structure the host built,
   * before any saved device state. `Reset Columns` restores from here.
   */
  private baseColumns: TrColumn[] = [];
  /** `stateId` this table is currently registered under, if any. */
  private registeredStateId?: string;
  /** Injected rather than taken as ctor params — the ctor is already at its limit. */
  private readonly registry = inject(TrTableRegistryService);
  private readonly layoutMode = inject(TrTableLayoutModeService);

  constructor(
    private readonly host: ElementRef<HTMLElement>,
    private readonly zone: NgZone,
    private readonly cdr: ChangeDetectorRef,
    private readonly modalService: FrontendModalService,
    private readonly tableState: TrTableStateService,
  ) {}

  ngOnInit(): void {
    this.watchViewportWidth();
    this.watchPageLeave();
  }

  /**
   * Start the table back at the top the next time its page is shown.
   *
   * `ion-router-outlet` keeps visited pages alive (hidden) in its stack, so a
   * table would otherwise come back exactly where it was left. Ionic dispatches
   * `ionViewWillLeave` on the leaving page element without bubbling, so listen in
   * the capture phase and act only when the leaving page holds this table. It
   * fires while the page is still laid out, before it is hidden — a
   * `display: none` element would ignore the scroll. Anything that scrolls to a
   * row on entry (the party highlight) runs after this, on the way back in.
   */
  private watchPageLeave(): void {
    if (typeof document === 'undefined') {
      return;
    }
    this.zone.runOutsideAngular(() => {
      document.addEventListener('ionViewWillLeave', this.onPageLeave, true);
    });
  }

  private readonly onPageLeave = (leave: Event): void => {
    const page = leave.target;
    if (!(page instanceof Node) || !page.contains(this.host.nativeElement)) {
      return;
    }
    this.resetScroll();
  };

  /** Scroll the rows viewport — columns or list layout — back to its origin. */
  resetScroll(): void {
    const selector = '.p-datatable-table-container, .tr-list';
    const viewports = this.host.nativeElement.querySelectorAll<HTMLElement>(selector);
    for (const el of Array.from(viewports)) {
      el.scrollTop = 0;
      el.scrollLeft = 0;
    }
  }

  /**
   * Track the viewport against {@link LIST_BREAKPOINT_PX} so the shell can swap to
   * the list layout.
   *
   * Width, not platform: the layout question is "are there enough pixels for
   * columns", which a rotated tablet answers differently from the same device in
   * portrait, and which a developer can reproduce by narrowing a window. A host
   * that needs to override the answer has `listView`.
   */
  private watchViewportWidth(): void {
    if (typeof window === 'undefined' || !window.matchMedia) {
      return;
    }
    this.viewportQuery = window.matchMedia(
      `(max-width: ${TrTableComponent.LIST_BREAKPOINT_PX - 0.02}px)`,
    );
    this.narrowViewport = this.viewportQuery.matches;
    this.zone.runOutsideAngular(() => {
      this.viewportQuery?.addEventListener('change', this.onViewportChange);
    });
  }

  private readonly onViewportChange = (event: MediaQueryListEvent): void => {
    this.zone.run(() => {
      this.narrowViewport = event.matches;
      this.cdr.detectChanges();
      // The layout that just came back is a fresh set of elements: re-bind the
      // scroll viewport and re-measure the pinned columns' sticky offsets.
      this.attachScrollListener();
      this.scheduleFrozenUpdate();
    });
  };

  /**
   * True when records should render as a stacked list rather than columns.
   *
   * The `listView` input wins when a host sets it; otherwise the viewport decides.
   */
  get listMode(): boolean {
    return this.listView ?? this.narrowViewport;
  }

  ngAfterContentInit(): void {
    // Covers tables whose `stateId` is a static attribute as well as bound ones.
    this.syncRegistration();
    this.rebuildTemplateMaps();
    this.contentSubs = new Subscription();
    this.contentSubs.add(this.cellDirs?.changes.subscribe(() => this.rebuildTemplateMaps()));
    this.contentSubs.add(this.headerDirs?.changes.subscribe(() => this.rebuildTemplateMaps()));
    // App-wide mode, so a table rendered later (or on another page) picks up the
    // state that is already on.
    this.contentSubs.add(
      this.layoutMode.editing$.subscribe((editing) => {
        this.layoutEditing = editing;
        this.cdr.markForCheck();
      }),
    );
  }

  ngAfterViewInit(): void {
    if (this.scrollHeight === 'fill') {
      this.fillHeight = new TrFillHeight(this.host.nativeElement, this.zone, (heightPx) => {
        this.fillHeightPx = heightPx;
        this.cdr.markForCheck();
      });
      this.fillHeight.start();
    }
    this.attachScrollListener();
    // Cells exist now — position the initially-pinned columns.
    this.scheduleFrozenUpdate();
  }

  /**
   * Listen to PrimeNG's scroll viewport for row windowing.
   *
   * Re-run whenever the column layout is (re)created, not just once: the viewport
   * belongs to the `p-table`, which is destroyed while the list layout is on
   * screen, so a phone rotated back into landscape would otherwise end up with a
   * table that never asks for more rows. In list mode there is nothing to attach —
   * `TrListComponent` watches its own viewport.
   */
  private attachScrollListener(): void {
    this.scrollEl?.removeEventListener('scroll', this.onScroll);
    this.scrollEl = undefined;
    if (!this.scrollable || this.listMode) {
      return;
    }
    this.scrollEl =
      this.host.nativeElement.querySelector<HTMLElement>('.p-datatable-table-container') ?? undefined;
    if (this.scrollEl) {
      this.zone.runOutsideAngular(() => {
        this.scrollEl?.addEventListener('scroll', this.onScroll, { passive: true });
      });
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.rows) {
      this.pendingLoad = false;
      // New/appended rows need the frozen `left` applied to their cells.
      this.scheduleFrozenUpdate();
    }
    if (changes.columns) {
      // The host rebuilt the structure — snapshot it, then lay the device's
      // saved order/visibility/pin/width back over it.
      this.baseColumns = (this.columns ?? []).map((col) => ({ ...col }));
      this.hydrateColumns();
    } else if (changes.stateId || changes.locationId) {
      // Different table or location: its state lives under a different key.
      this.hydrateColumns();
    }
    if (changes.stateId) {
      this.syncRegistration();
    }
  }

  ngOnDestroy(): void {
    this.registry.unregister(this.registeredStateId, this);
    this.viewportQuery?.removeEventListener('change', this.onViewportChange);
    document.removeEventListener('ionViewWillLeave', this.onPageLeave, true);
    this.scrollEl?.removeEventListener('scroll', this.onScroll);
    this.fillHeight?.stop();
    this.detachResizeListeners();
    this.detachHeaderListeners();
    this.contentSubs?.unsubscribe();
    if (this.frozenRaf) {
      cancelAnimationFrame(this.frozenRaf);
    }
  }

  /** Keep the gear button's lookup pointing at this table's current `stateId`. */
  private syncRegistration(): void {
    if (this.registeredStateId === this.stateId) {
      return;
    }
    this.registry.unregister(this.registeredStateId, this);
    this.registry.register(this.stateId, this);
    this.registeredStateId = this.stateId;
  }

  // ----- device column state (localStorage) --------------------------------

  /**
   * Merge the device's saved state over the host's pristine column structure.
   *
   * The table only ever renders from storage, so a device with nothing stored is
   * seeded first (see {@link seedStoredState}).
   */
  private hydrateColumns(): void {
    this.seedStoredState();
    this.columns = this.tableState.apply(
      this.baseColumns,
      this.tableState.read(this.locationId, this.stateId),
    );

    this.recomputeDisplay();
  }

  /**
   * Give a device with no stored layout its starting state, once.
   *
   * The location default wins when there is one; otherwise the column set the
   * host built is the default. Either way it is written to storage at once, so
   * a location default saved later (by anyone) never replaces a layout this
   * device has already started from — only "Reset to Location Default" does.
   * A no-op until both `stateId` and `locationId` are known, which is also when
   * the host's location (and so `columnsToSave`) has loaded.
   */
  private seedStoredState(): void {
    if (this.tableState.read(this.locationId, this.stateId)) {
      return;
    }
    const defaults = this.columnsToSave?.length
      ? withoutWidths(this.columnsToSave)
      : this.baseColumns;
    // Nothing built yet — seeding now would store an empty layout.
    if (defaults.length) {
      this.tableState.save(this.locationId, this.stateId, defaults);
    }
  }

  /** Write the current order/visibility/pin/width for this device. */
  private persistColumns(): void {
    this.tableState.save(this.locationId, this.stateId, this.columns);
  }

  /**
   * Drop this device's copy — order, visibility, pin and widths — and re-seed it
   * from the location default, or the app defaults when the location has none.
   * Widths go back to the default sizing either way: they are never part of a
   * location default.
   *
   * The saved default supplies order/visibility/pin only; *which* columns
   * exist still comes from the host's current build. Restoring the stored objects
   * wholesale would resurrect a column the settings have since switched off (a
   * location that saved its layout while Quoted Time was on would get it back,
   * show/hide modal included) and would show whatever labels were current the day
   * it was saved. Clearing first keeps the stored state exactly the default —
   * leftovers from this device don't survive a reset.
   */
  resetToLocationDefault(): void {
    this.tableState.clear(this.locationId, this.stateId);
    // Re-seeds from `columnsToSave` or the built columns before re-rendering.
    this.hydrateColumns();
    this.layoutMode.clearDirty();
    this.cdr.markForCheck();
  }

  /**
   * Publish this device's column layout as the location's default.
   *
   * The only path that writes to the location: `columnsUpdated` fires here and
   * nowhere else, so ordinary hiding, pinning and resizing stay on the device
   * (localStorage) and one host's preference never becomes everyone's. The host
   * turns the event into the backend call — see the waitlist's `updateColumns`.
   * Other devices pick it up only where they have no layout stored yet, or on
   * "Reset to Location Default" (see {@link seedStoredState}).
   */
  saveAsLocationDefault(): void {
    // Order, visibility and pin only — this device's widths stay on this device.
    this.columnsUpdated.emit(withoutWidths(this.columns));
    this.layoutMode.clearDirty();
  }

  private rebuildTemplateMaps(): void {
    this.cellTemplates = new Map(
      (this.cellDirs ?? []).map((d): [string, TrCellDirective] => [d.key, d]),
    );
    this.headerTemplates = new Map(
      (this.headerDirs ?? []).map((d): [string, TrHeaderCellDirective] => [d.key, d]),
    );
    this.cdr.markForCheck();
  }

  /** The rendered `<table>` element (e.g. for printing). Null before first render. */
  getTableElement(): HTMLElement | null {
    return this.host.nativeElement.querySelector('table');
  }

  /**
   * The `<table>` to print, rendering one if the list layout is showing.
   *
   * Print copies a real table (the web clone and the native printer's HTML both
   * need `<table>` markup), but a phone gets the stacked list, which has none. So
   * in list mode the column layout is also rendered off-screen until
   * {@link endPrint} — the visible layout never changes under the user.
   */
  preparePrint(): HTMLElement | null {
    if (this.listMode && !this.printSource) {
      this.printSource = true;
      this.cdr.detectChanges();
    }

    return this.getTableElement();
  }

  /** Drop the off-screen table {@link preparePrint} rendered, once it's been copied. */
  endPrint(): void {
    if (this.printSource) {
      this.printSource = false;
      this.cdr.markForCheck();
    }
  }

  /**
   * Height for PrimeNG's scroll viewport.
   *
   * The card is inset from the page and draws its own border (together
   * `--tr-frame-y`), and both cost layout space the host didn't budget for —
   * `scrollHeight="calc(100vh - 7.5rem)"` would end up that much taller than the
   * room it was given and push the page into scrolling. Give the frame straight
   * back here so the card's footprint matches the pre-card layout exactly, and
   * the visible margin is the only difference.
   */
  get viewportHeight(): string | undefined {
    if (!this.scrollable) {
      return undefined;
    }
    // 'flex' fills the parent, which already accounts for the margin.
    if (!this.scrollHeight || this.scrollHeight === 'flex') {
      return this.scrollHeight;
    }
    // 'fill' is measured as the card's outer height; until the first measurement lands
    // (a frame after render), the old page budget stands in.
    let outer = this.scrollHeight;
    if (outer === 'fill') {
      outer = this.fillHeightPx ? `${this.fillHeightPx}px` : '100vh - 7.5rem';
    }

    return `calc(${outer} - var(--tr-frame-y, calc(1.5rem + 2px)))`;
  }

  trackByColKey = (_: number, col: TrColumn): string => col.key;

  trackByRow = (index: number, row: T): unknown =>
    this.trackBy ? this.trackBy(index, row) : (row as Record<string, unknown>)?.[this.dataKey];

  /** Fallback cell value when a column has no projected `*trCell` (uses `field`). */
  fieldValue(row: T, field?: string): unknown {
    return field ? (row as Record<string, unknown>)?.[field] : '';
  }

  rowIdFor(row: T): string | null {
    return this.rowId ? this.rowId(row) : null;
  }

  rowClassFor(row: T): string | null | undefined {
    return this.rowClass ? this.rowClass(row) : null;
  }

  isRowHidden(row: T): boolean {
    return this.rowHidden ? this.rowHidden(row) : false;
  }

  /** Whether this row is the highlighted one (its data key matches `highlightedId`). */
  isHighlighted(row: T): boolean {
    return (
      this.highlightedId != null &&
      (row as Record<string, unknown>)?.[this.dataKey] === this.highlightedId
    );
  }

  // ----- group rows --------------------------------------------------------

  groupKeyAt(index: number): string | null {
    if (!this.groupBy || !this.rows[index]) {
      return null;
    }
    return this.groupBy(this.rows[index], index, this.rows);
  }

  /** True when row `index` begins a new group (or is the first row). */
  isGroupBoundary(index: number): boolean {
    if (!this.groupBy) {
      return false;
    }
    const current = this.groupKeyAt(index);
    if (current == null) {
      return false;
    }
    return index === 0 || this.groupKeyAt(index - 1) !== current;
  }

  onOrderBy(value: OrderByValues): void {
    this.orderByChange.emit(value);
  }

  // ----- column menu -------------------------------------------------------

  openColumnMenu(event: MouseEvent, col: TrColumn): void {
    event.stopPropagation();
    this.menuModel = this.buildMenu(col);
    this.colMenu?.toggle(event);
  }

  private buildMenu(col: TrColumn): MenuItem[] {
    const items: MenuItem[] = [];

    // Flat, never a submenu: two sort directions don't earn a second level the
    // pointer has to travel into.
    if (col.sortKey) {
      items.push({
        label: 'Sort Ascending',
        icon: 'bi bi-sort-down-alt',
        command: () => this.sort(col, true),
      });
      items.push({
        label: 'Sort Descending',
        icon: 'bi bi-sort-up',
        command: () => this.sort(col, false),
      });
    }

    if (col.pin !== 'start') {
      items.push({ label: 'Pin Left', icon: 'bi bi-pin-angle', command: () => this.pinColumn(col, 'start') });
    }
    if (col.pin !== 'end') {
      items.push({ label: 'Pin Right', icon: 'bi bi-pin-angle', command: () => this.pinColumn(col, 'end') });
    }
    if (col.pin) {
      items.push({ label: 'Unpin', icon: 'bi bi-pin-angle-fill', command: () => this.unpin(col) });
    }

    if (col.resizable !== false) {
      items.push({ label: 'Auto Fit', icon: 'bi bi-arrow-left-right', command: () => this.autoFit(col) });
      items.push({ label: 'Reset Width', icon: 'bi bi-arrow-counterclockwise', command: () => this.resetWidth(col) });
    }

    // Table-wide items (Show/Hide Columns, location defaults) deliberately stay
    // out of here — they live in the toolbar's table settings gear.
    items.push({ label: 'Hide Column', icon: 'bi bi-eye-slash', command: () => this.hide(col) });

    return items;
  }

  private sort(col: TrColumn, ascending: boolean): void {
    if (col.sortKey) {
      this.orderByChange.emit({ orderBy: col.sortKey, orderAscending: ascending });
    }
  }

  pinColumn(col: TrColumn, side: 'start' | 'end'): void {
    col.pin = side;
    this.recomputeDisplay();
    this.persistColumns();
    this.layoutMode.markDirty();
  }

  unpin(col: TrColumn): void {
    col.pin = undefined;
    this.recomputeDisplay();
    this.persistColumns();
    this.layoutMode.markDirty();
  }

  hide(col: TrColumn): void {
    this.setColumnHidden(col.key, true);
  }

  /**
   * Hide or show one column by key.
   *
   * Keyed rather than by reference so a caller that is holding an older column
   * object — the show/hide modal, which stays open across rebuilds — still lands
   * on the column this table is rendering right now.
   */
  setColumnHidden(key: string, hidden: boolean): void {
    const col = this.columns.find((c) => c.key === key);
    if (!col || col.hidden === hidden) {
      return;
    }
    // Never hide the last remaining column, or the table has nothing to show.
    if (hidden && this.columns.filter((c) => !c.hidden).length <= 1) {
      return;
    }
    col.hidden = hidden;
    this.afterColumnChange();
  }

  /** Reveal every column, hidden or not. */
  showAllColumns(): void {
    if (this.columns.every((col) => !col.hidden)) {
      return;
    }
    this.columns.forEach((col) => (col.hidden = false));
    this.afterColumnChange();
  }

  // ----- show/hide columns modal -------------------------------------------

  openColumnsModal(): void {
    this.modalService
      .create<ColumnVisibilityModalComponent, unknown>(ColumnVisibilityModalComponent, {
        // A getter, not a snapshot: `this.columns` is replaced whenever the host
        // rebuilds (the waitlist does so on every change, via the location save
        // coming back as a `locationUpdated` event), and a modal holding the old
        // array would mutate objects this table no longer renders.
        getColumns: (): TrColumn[] => this.columns,
        setHidden: (key: string, hidden: boolean): void => this.setColumnHidden(key, hidden),
        showAllColumns: (): void => this.showAllColumns(),
        hints: this.columnHints,
      })
      .pipe(take(1))
      .subscribe();
  }

  /** Shared tail for a column visibility change: re-layout, persist, mark dirty. */
  private afterColumnChange(): void {
    this.recomputeDisplay();
    this.persistColumns();
    this.layoutMode.markDirty();
    this.cdr.markForCheck();
  }

  resetWidth(col: TrColumn): void {
    col.width = undefined;
    // A frozen column losing its explicit width shifts every frozen column after
    // it — re-measure once the cells fall back to their natural size.
    this.scheduleFrozenUpdate();
    this.persistColumns();
  }

  autoFit(col: TrColumn): void {
    // Release the width so the cells fall back to their natural size, measure,
    // then pin to the widest content.
    col.width = undefined;
    this.cdr.detectChanges();

    const index = this.displayColumns.indexOf(col);
    if (index < 0) {
      return;
    }
    const nth = index + 1;
    // Exclude full-width group rows (single colspan cell) from the measurement.
    const cells = this.host.nativeElement.querySelectorAll<HTMLElement>(
      `.tr-table thead > tr > th:nth-child(${nth}), ` +
        `.tr-table tbody > tr:not(.tr-group-row) > td:nth-child(${nth})`,
    );
    let max = TrTableComponent.MIN_COL_WIDTH;
    cells.forEach((c) => {
      max = Math.max(max, c.scrollWidth);
    });
    col.width = Math.ceil(max) + TrTableComponent.AUTO_FIT_PADDING;
    this.scheduleFrozenUpdate();
    this.persistColumns();
  }

  // ----- drag reorder (pointer-based, so it works with mouse AND touch) ------

  onHeaderPointerDown(event: PointerEvent, col: TrColumn): void {
    // Reordering is part of editing the layout: outside that mode a drag across
    // the header is far more likely to be a stray gesture than an intent to move
    // a column, and the header gives no affordance for it (see `layoutEditing`).
    if (!this.layoutEditing) {
      return;
    }
    const target = event.target as HTMLElement;
    // Primary button/touch only, and never when starting on the resizer or the
    // options menu (they own their own gestures).
    if (event.button > 0 || target.closest('.tr-col__resizer') || target.closest('.tr-col__menu')) {
      return;
    }
    this.reorderState = { col, startX: event.clientX, startY: event.clientY, active: false };
    this.zone.runOutsideAngular(() => {
      document.addEventListener('pointermove', this.onHeaderPointerMove);
      document.addEventListener('pointerup', this.onHeaderPointerUp);
      document.addEventListener('pointercancel', this.onHeaderPointerUp);
    });
  }

  private readonly onHeaderPointerMove = (event: PointerEvent): void => {
    const state = this.reorderState;
    if (!state) {
      return;
    }
    if (!state.active) {
      const moved = Math.hypot(event.clientX - state.startX, event.clientY - state.startY);
      if (moved < TrTableComponent.DRAG_THRESHOLD_PX) {
        return;
      }
      state.active = true;
      this.zone.run(() => (this.dragColKey = state.col.key));
    }
    // Stop the page/table from scrolling or text-selecting mid-drag (esp. touch).
    event.preventDefault();
    const target = this.columnAtPoint(event.clientX, event.clientY);
    const overKey =
      target && target.pin === state.col.pin && target.key !== state.col.key ? target.key : null;
    if (overKey !== this.dragOverKey) {
      this.zone.run(() => (this.dragOverKey = overKey));
    }
  };

  private readonly onHeaderPointerUp = (): void => {
    const state = this.reorderState;
    this.reorderState = undefined;
    this.detachHeaderListeners();
    if (state?.active) {
      if (this.dragOverKey) {
        this.zone.run(() => this.reorderColumn(state.col.key, this.dragOverKey as string));
      }
      // A drag just ended — swallow the click it would otherwise fire (sort).
      this.swallowNextClick();
    }
    this.zone.run(() => {
      this.dragColKey = null;
      this.dragOverKey = null;
    });
  };

  private detachHeaderListeners(): void {
    document.removeEventListener('pointermove', this.onHeaderPointerMove);
    document.removeEventListener('pointerup', this.onHeaderPointerUp);
    document.removeEventListener('pointercancel', this.onHeaderPointerUp);
  }

  private swallowNextClick(): void {
    const swallow = (e: Event): void => {
      e.stopPropagation();
      e.preventDefault();
      document.removeEventListener('click', swallow, true);
    };
    document.addEventListener('click', swallow, true);
  }

  /** Header column whose cell sits under the given viewport point, if any. */
  private columnAtPoint(x: number, y: number): TrColumn | undefined {
    const th = (document.elementFromPoint(x, y) as HTMLElement | null)?.closest('th.tr-col') as HTMLElement | null;
    if (!th) {
      return undefined;
    }
    const cells = Array.from(
      this.host.nativeElement.querySelectorAll<HTMLElement>('.tr-table thead > tr > th.tr-col'),
    );
    const index = cells.indexOf(th);
    return index >= 0 ? this.displayColumns[index] : undefined;
  }

  /** Move the dragged column to the target's slot within the master `columns`. */
  private reorderColumn(fromKey: string | null, toKey: string): void {
    if (!fromKey || fromKey === toKey) {
      return;
    }
    const cols = [...this.columns];
    const from = cols.findIndex((c) => c.key === fromKey);
    const to = cols.findIndex((c) => c.key === toKey);
    // Only reorder within the same group (start-pinned / unpinned / end-pinned).
    if (from < 0 || to < 0 || cols[from].pin !== cols[to].pin) {
      return;
    }
    const [moved] = cols.splice(from, 1);
    // Re-find the target after removal, then insert on the side we dragged from.
    const insertAt = cols.findIndex((c) => c.key === toKey);
    cols.splice(from < to ? insertAt + 1 : insertAt, 0, moved);
    this.columns = cols;
    this.recomputeDisplay();
    this.persistColumns();
    this.layoutMode.markDirty();
  }

  // ----- resize (pointer-based, owned so it survives pin/reorder) ----------

  startResize(event: PointerEvent, col: TrColumn): void {
    event.preventDefault();
    event.stopPropagation();
    const th = (event.target as HTMLElement).closest('th');
    const startWidth = col.width ?? (th ? Math.round(th.getBoundingClientRect().width) : 100);
    this.resizeState = { col, startX: event.clientX, startWidth };
    this.zone.runOutsideAngular(() => {
      document.addEventListener('pointermove', this.onResizeMove);
      document.addEventListener('pointerup', this.onResizeEnd);
      document.addEventListener('pointercancel', this.onResizeEnd);
    });
  }

  private readonly onResizeMove = (event: PointerEvent): void => {
    if (!this.resizeState) {
      return;
    }
    event.preventDefault();
    const delta = event.clientX - this.resizeState.startX;
    const width = Math.max(TrTableComponent.MIN_COL_WIDTH, this.resizeState.startWidth + delta);
    this.zone.run(() => {
      if (this.resizeState) {
        this.resizeState.col.width = Math.round(width);
        // Resizing a pinned column shifts its neighbours' sticky offsets — keep
        // them in lock-step with the drag, this frame.
        this.computeFrozenOffsets();
      }
    });
  };

  private readonly onResizeEnd = (): void => {
    const resized = !!this.resizeState;
    this.resizeState = undefined;
    this.detachResizeListeners();
    if (resized) {
      // Widths are device-local: saved here, never sent to the backend.
      this.persistColumns();
    }
  };

  private detachResizeListeners(): void {
    document.removeEventListener('pointermove', this.onResizeMove);
    document.removeEventListener('pointerup', this.onResizeEnd);
    document.removeEventListener('pointercancel', this.onResizeEnd);
  }

  // ----- column layout -----------------------------------------------------

  private recomputeDisplay(): void {
    const visible = this.columns.filter((c) => !c.hidden);
    // Layout: start-pinned | unpinned | end-pinned, each keeping its original order.
    const start = visible.filter((c) => c.pin === 'start');
    const end = visible.filter((c) => c.pin === 'end');
    const middle = visible.filter((c) => !c.pin);
    this.displayColumns = [...start, ...middle, ...end];
    this.visibleKeys = new Set(this.displayColumns.map((c) => c.key));
    this.lastStartKey = start.length ? start[start.length - 1].key : null;
    this.firstEndKey = end.length ? end[0].key : null;
    // Order/pin state changed — recompute sticky offsets once the DOM reflects it.
    this.scheduleFrozenUpdate();
  }

  // ----- frozen (sticky) offsets, owned by this component ------------------

  /** Recompute after the next render, when cell widths reflect the new layout. */
  private scheduleFrozenUpdate(): void {
    if (this.frozenRaf) {
      return;
    }
    this.frozenRaf = this.zone.runOutsideAngular(() =>
      requestAnimationFrame(() => {
        this.frozenRaf = 0;
        this.zone.run(() => {
          this.computeFrozenOffsets();
          this.cdr.markForCheck();
        });
      }),
    );
  }

  /**
   * Sticky offsets for both pinned edges. Start-pinned columns are the leading
   * run of `displayColumns` (cumulative `left` from the left); end-pinned columns
   * are the trailing run (cumulative `right` from the right). Uses the explicit
   * width when set, else the measured header-cell width (content-sized columns).
   */
  private computeFrozenOffsets(): void {
    const headerCells = this.host.nativeElement.querySelectorAll<HTMLElement>(
      '.tr-table thead > tr > th.tr-col',
    );
    const widthOf = (col: TrColumn, i: number): number =>
      col.width ?? headerCells[i]?.getBoundingClientRect().width ?? TrTableComponent.MIN_COL_WIDTH;

    const left: Record<string, number> = {};
    for (let i = 0, running = 0; i < this.displayColumns.length; i++) {
      const col = this.displayColumns[i];
      if (col.pin !== 'start') {
        break; // start-pinned columns are a contiguous prefix
      }
      left[col.key] = running;
      running += widthOf(col, i);
    }

    const right: Record<string, number> = {};
    for (let i = this.displayColumns.length - 1, running = 0; i >= 0; i--) {
      const col = this.displayColumns[i];
      if (col.pin !== 'end') {
        break; // end-pinned columns are a contiguous suffix
      }
      right[col.key] = running;
      running += widthOf(col, i);
    }

    this.frozenLeft = left;
    this.frozenRight = right;
  }

  private readonly onScroll = (): void => {
    if (!this.scrollEl || !this.hasMore || this.pendingLoad) {
      return;
    }
    const { scrollTop, clientHeight, scrollHeight } = this.scrollEl;
    if (scrollTop + clientHeight >= scrollHeight - TrTableComponent.LOAD_MORE_THRESHOLD_PX) {
      this.pendingLoad = true;
      this.zone.run(() => this.loadMore.emit());
    }
  };
}
